import { decodeHtmlCharCode } from './html'

const NBSP = '\xA0'

// コードフェンス（``` または ~~~）の開始行にマッチ
export const CODE_FENCE_RE = /^(`{3,}|~{3,})/

// Markdown ブロック要素の開始/終了行にマッチ。
export const BLOCK_ELEMENT_RE =
  /^(`{3,}|~{3,}|#{1,6}\s|\s*[-*+]\s|\s*\d+\.\s|>\s?|\|)|^(---+|___+|\*\*\*+)\s*$/

const findNearestNonEmptyLine = (
  lines: string[],
  from: number,
  direction: -1 | 1
): string => {
  let i = from + direction
  while (i >= 0 && i < lines.length) {
    if (lines[i] !== '') return lines[i]
    i += direction
  }
  return ''
}

export const normalize = (value: string) => {
  return decodeHtmlCharCode(value)
}

// 保存値（プレーン形式: \n=改行、空行=可視の空行）をエディタ用のマーカー形式へ変換する。
// 空行を \xA0 マーカーに変換し、リッチテキストで空段落として表示できるようにする。
// ブロック要素境界では1行を構造的区切り（Markdown構文上必要）として残し、
// 残りを \xA0 に変換する。コードブロック内の空行は変換しない。
export const promoteBlankLinesToMarkers = (plain: string): string => {
  if (!plain) return plain
  const lines = plain.split('\n')
  const result = [...lines]
  let inCodeBlock = false

  let i = 0
  while (i < lines.length) {
    if (CODE_FENCE_RE.test(lines[i])) {
      inCodeBlock = !inCodeBlock
    }
    if (inCodeBlock || lines[i] !== '') {
      i++
      continue
    }

    const runStart = i
    while (i < lines.length && lines[i] === '') {
      i++
    }
    const runEnd = i

    const prev = findNearestNonEmptyLine(lines, runStart, -1)
    const next = findNearestNonEmptyLine(lines, runEnd - 1, 1)
    // 構造的区切りの温存は、前後両方にコンテンツが実在するときだけ。
    // 文書端に接する空行は区切る相手がいないため、すべて可視の空行として扱う
    // （区切りを温存すると切替往復で空行数が振動する）。
    const nearBlock =
      prev !== '' &&
      next !== '' &&
      (BLOCK_ELEMENT_RE.test(prev) || BLOCK_ELEMENT_RE.test(next))

    const markerEnd = nearBlock ? runEnd - 1 : runEnd
    for (let j = runStart; j < markerEnd; j++) {
      result[j] = NBSP
    }
  }

  return result.join('\n')
}

// エディタ内部値（collapsed マーカー形式）を保存用のプレーン形式へ変換する。
// promoteBlankLinesToMarkers の逆変換:
// - \xA0 マーカー行 → 可視の空行。ブロック要素境界では構造的区切り1行を補う
// - マーカーに隣接しない空行 → ブロック要素境界なら構造的区切りとして残し、
//   通常段落間の区切りなら除去する（旧実装は隣接段落を \n 1つで連結していた）
export const demoteMarkersToBlankLines = (md: string): string => {
  const lines = md.split('\n')
  const result: string[] = []
  let inCodeBlock = false

  let i = 0
  while (i < lines.length) {
    if (CODE_FENCE_RE.test(lines[i])) {
      inCodeBlock = !inCodeBlock
    }
    if (inCodeBlock || (lines[i] !== '' && lines[i] !== NBSP)) {
      result.push(lines[i])
      i++
      continue
    }

    if (lines[i] === NBSP) {
      const runStart = i
      while (i < lines.length && lines[i] === NBSP) {
        i++
      }
      const markerCount = i - runStart
      const prev = findNearestNonEmptyLine(lines, runStart, -1)
      const next = findNearestNonEmptyLine(lines, i - 1, 1)
      // 構造的区切りの補完も promote と対称に、前後両方にコンテンツが
      // 実在するときだけ行う（文書端では区切る相手がいない）。
      const nearBlock =
        prev !== '' &&
        next !== '' &&
        (BLOCK_ELEMENT_RE.test(prev) || BLOCK_ELEMENT_RE.test(next))
      const blankCount = nearBlock ? markerCount + 1 : markerCount
      for (let j = 0; j < blankCount; j++) {
        result.push('')
      }
      continue
    }

    // 区切り空行の連なりはまとめて処理する。getMarkdown はテーブル等の前後に
    // 余分な改行を出すことがあり、1行ずつ温存すると可視の空行に化けるため。
    // 文書の先頭・末尾に接する連なりは構造的区切りではないため除去する
    // （意図的な先頭・末尾の空行は \xA0 マーカーとして渡ってくる）。
    const runStart = i
    while (i < lines.length && lines[i] === '') {
      i++
    }
    const prev = findNearestNonEmptyLine(lines, runStart, -1)
    const next = findNearestNonEmptyLine(lines, i - 1, 1)
    if (
      prev !== '' &&
      next !== '' &&
      (BLOCK_ELEMENT_RE.test(prev) || BLOCK_ELEMENT_RE.test(next))
    ) {
      result.push('')
    }
  }

  return result.join('\n')
}

// ソースエディタ表示用: 空段落マーカー(\xA0)前後の余分な空行を除去し、
// リッチエディタと同じ見た目（空段落1つ=空行1本）にする。
// "a\n\n\xA0\n\nb" → "a\n\xA0\nb"
// テーブル等が末尾に余分な改行を持つケースでも1パスで畳み切る。
export const collapseEmptyParagraphMarkers = (md: string): string => {
  const lines = md.split('\n')
  const result: string[] = []
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] !== '') {
      result.push(lines[i])
      continue
    }
    let j = i
    while (j < lines.length && lines[j] === '') j++
    const followedByMarker = j < lines.length && lines[j] === NBSP
    const precededByMarker =
      result.length > 0 && result[result.length - 1] === NBSP
    if (followedByMarker || precededByMarker) {
      continue
    }
    result.push(lines[i])
  }
  return result.join('\n')
}

// リッチエディタ parse 用: 畳んだ空段落マーカーを Markdown として有効な形に展開する。
// マーカー行の前後に空行(\n\n)がないとき挿入し、marked が段落区切りとして認識できるようにする。
// "a\n\xA0\nb" → "a\n\n\xA0\n\nb"
export const expandEmptyParagraphMarkers = (md: string): string => {
  const lines = md.split('\n')
  const result: string[] = []
  for (let i = 0; i < lines.length; i++) {
    if (lines[i] === NBSP) {
      if (result.length > 0 && result[result.length - 1] !== '') {
        result.push('')
      }
      result.push(lines[i])
      if (i + 1 < lines.length && lines[i + 1] !== '') {
        result.push('')
      }
    } else {
      result.push(lines[i])
    }
  }
  return result.join('\n')
}
